import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useRouteMatch, useParams } from "react-router-dom"
import { Table, Tooltip, Skeleton, notification, Button, Dropdown, Menu } from "antd"
import { DownloadOutlined, PlusOutlined, UploadOutlined, EditOutlined, SearchOutlined, FilterOutlined, DownOutlined, EyeOutlined, DeleteOutlined, BookOutlined } from "@ant-design/icons"
import nodePath from "path"

import { fetchBillableAccounts, selectBillableAccounts, deleteBillableAccount, selectTotal, selectPagination, selectLoading, filterBillableAccounts, generateCsv, selectFilters, selectSort, setFilters, setSort } from "../billableAccountsSlice"
import { fetchSubaccounts, selectSubaccounts } from "../../groupAccounts/groupAccountsSlice"
import { allObjectValuesEmpty } from "../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnSearchFilter from "../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnToggleFilter from "../../../components/utilityComponents/table/ColumnToggleFilter"
import ColumnHeaderTitle from "../../../components/utilityComponents/table/ColumnHeaderTitle"
import { tableFiltersApplied } from "../../../functions/objectFunctions/tableFiltersApplied"
import ColumnDateFilter from "../../../components/utilityComponents/table/ColumnDateFilter"
import moment from "moment"
import useConfirmationModal from "../../alertsPanel/ShowConfirm"

import Role from "../../userRoles/Role"
import { getIcon } from "../../../lib/utilities"
import { selectRoles } from "../../userRoles/userRolesSlice"

export default function BillableAccountTable({ scopedByClient }) {
    /////*** react router start ***/////
    const { url } = useRouteMatch()
    const { clientId, groupAccountId, facilityId } = useParams()
    /////*** react router end ***/////

    /////*** redux start ***/////
    const dispatch = useDispatch()
    const billableAccounts = useSelector(selectBillableAccounts)
    const subaccounts = useSelector(selectSubaccounts)
    const total = useSelector(selectTotal)
    const pagination = useSelector(selectPagination)
    const loading = useSelector(selectLoading)
    const filters = useSelector(selectFilters)
    const sort = useSelector(selectSort)
    const roles = useSelector(selectRoles)
    /////*** redux end ***/////

    /////*** useState start ***/////
    const [loaded, setLoaded] = useState(false)
    // const [filters, setFilters] = useState({})
    // const [sort, setSort] = useState({
    //     column: "name",
    //     direction: "asc",
    // })
    /////*** useState end ***/////

    /////*** useEffects start ***/////
    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialBillableAccounts(true)
        }
    }, [loaded, url, dispatch, pagination]) // eslint-disable-line
    /////*** useEffects end ***/////

    /////*** event handler methods start ***/////
    const handleFetchInitialBillableAccounts = (withFilters = false) => {
        if(!withFilters){
            if (!!groupAccountId) {
                dispatch(fetchSubaccounts(pagination, nodePath.join(url, "")))
            } else {
                dispatch(fetchBillableAccounts(pagination, nodePath.join(url, "")))
            }
        }
        else{
            dispatch(
                filterBillableAccounts(
                    {
                        clientId: clientId,
                        filters: filters,
                        pagination: pagination,
                        sort: sort,
                    },
                    { facilityId: facilityId }
                )
            )
        }
    }
    
    const showConfirm = useConfirmationModal();
    const handleDeleteClick = (id) => {
        showConfirm(deleteBillableAccount, id);
    };

    const handleFilterBillableAccounts = (newPagination = null, newSort = null) => {
        let paggy = !!newPagination ? newPagination : pagination
        return dispatch(
            filterBillableAccounts(
                {
                    clientId: clientId,
                    filters: filters,
                    pagination: !!newPagination ? newPagination : pagination,
                    sort: !!newSort ? newSort : sort,
                },
                { facilityId: facilityId }
            )
        )
    }

    const handleSortBillableAccountsChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        dispatch(setSort(newSort))
        handleFilterBillableAccounts(null, newSort)
    }

    const handleTableChange = (newPagination, sorter) => {
        if (allObjectValuesEmpty(filters) && allObjectValuesEmpty(sorter)) {
            if (groupAccountId) {
                dispatch(fetchSubaccounts(newPagination, nodePath.join(url, "")))
            } else {
                dispatch(fetchBillableAccounts(newPagination, nodePath.join(url, "")))
            }
        } else {
            handleFilterBillableAccounts(newPagination)
        }
    }

    const billableAccountsCsv = () => {
        notification.open({
            message: "Billable accounts export started",
            duration: 2,
        })

        dispatch(generateCsv(clientId, filters))
    }
    /////*** event handler methods end ***/////

    /////*** render methods start ***/////
    const renderColumnHeaderTitle = (title, altTitle = null) => {
        return <ColumnHeaderTitle title={title} altTitle={altTitle} handleSortChange={handleSortBillableAccountsChange} sort={sort} />
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters)) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a
                    href="javascript:;"
                    onClick={() => {
                        handleFetchInitialBillableAccounts()
                        dispatch(setFilters({}))
                        dispatch(setSort({ column: "name", direction: "asc" }))
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }

    const renderTableActions = () => {
        if (groupAccountId) {
            return (
                <div className="actions">
                    <div className="table-action-links">
                        {renderResetFilters()}

                        <Link to={`/clients/${scopedByClient}/group_accounts/${groupAccountId}/edit`}>
                            <PlusOutlined />
                            Manage
                        </Link>
                    </div>
                </div>
            )
        } else {
            return (
                <div className="actions">
                    <div className="table-action-links">
                        {renderResetFilters()}

                        <Role action="create" model="billable_account">
                            <Link to={scopedByClient ? `/clients/${scopedByClient}/billable_accounts/new` : `/billable_accounts/new`}>
                                <PlusOutlined />
                                Add New
                            </Link>
                        </Role>

                        <Role action="update" model="billable_account_import">
                            <Link to={`${url}/import`}>
                                <PlusOutlined />
                                Import
                            </Link>
                        </Role>

                        {scopedByClient && (
                            <Role action="update" model="bulk_edit">
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            <Menu.Item key="accounts">
                                                <Link to={`/clients/${scopedByClient}/billable_accounts/bulk_edit`}>Accounts</Link>
                                            </Menu.Item>
                                            <Menu.Item key="services">
                                                <Link to={`/clients/${scopedByClient}/billable_accounts/bulk_edit_services`}>Services</Link>
                                            </Menu.Item>
                                        </Menu>
                                    }>
                                    <a href="javascript:;">
                                        <EditOutlined />
                                        Bulk Edit <DownOutlined />
                                    </a>
                                </Dropdown>
                            </Role>
                        )}

                        <a href="javascript:;" onClick={() => billableAccountsCsv()}>
                            <DownloadOutlined />
                            Export
                        </a>
                    </div>
                </div>
            )
        }
    }
    /////*** render methods end ***/////
    return (
        <div className="muc-table card">
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>{groupAccountId ? "Subaccounts" : "Billable Accounts"}</h2>
                            <div className="badge">{total}</div>
                        </div>

                        {renderTableActions()}
                    </div>

                    <Skeleton active loading={loading}>
                        <Table
                            dataSource={groupAccountId ? subaccounts : billableAccounts}
                            pagination={{ ...pagination, showSizeChanger: true, total: total, pageSizeOptions: [10, 20, 50, 100, 200] }}
                            onChange={handleTableChange}
                            rowKey={(r) => r.id}>
                            <Table.Column
                                title={renderColumnHeaderTitle("facility_name", "facility")}
                                key="facility_id"
                                dataIndex="facility"
                                render={(facility) => facility?.name}
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"facility_name"}
                                        filterType={"facility_name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("facility_name_cont")}
                            />
                            {/* {facilityId && (
                                <> */}
                                    <Table.Column
                                        // title={renderColumnHeaderTitle("facility_type")}
                                        title="Facility Type"
                                        key="facility_type"
                                        dataIndex="facility_type"
                                        // render={(facility) => facility?.number}
                                        // filterDropdown={({ visible }) => (
                                        //     <ColumnSearchFilter
                                        //         handleFetchInitial={handleFetchInitialBillableAccounts}
                                        //         filterAction={filterBillableAccounts}
                                        //         filterColumn={"facility_type"}
                                        //         filterType={"facility_type_cont"}
                                        //         filters={filters}
                                        //         pagination={pagination}
                                        //         setFilters={setFilters}
                                        //         sort={sort}
                                        //         setSort={setSort}
                                        //         facilityId={facilityId}
                                        //         visible={visible}
                                        //         useDispatch
                                        //     />
                                        // )}
                                        // filterIcon={() => renderSearchOutlinedButton("facility_type_cont")}
                                    />
                                    <Table.Column
                                        // title={renderColumnHeaderTitle("division_name")}
                                        title="Division Name"
                                        key="division_name"
                                        dataIndex="division_name"
                                        // render={(facility) => facility?.number}
                                        // filterDropdown={({ visible }) => (
                                        //     <ColumnSearchFilter
                                        //         handleFetchInitial={handleFetchInitialBillableAccounts}
                                        //         filterAction={filterBillableAccounts}
                                        //         filterColumn={"division_name"}
                                        //         filterType={"division_name_cont"}
                                        //         filters={filters}
                                        //         pagination={pagination}
                                        //         setFilters={setFilters}
                                        //         sort={sort}
                                        //         setSort={setSort}
                                        //         facilityId={facilityId}
                                        //         visible={visible}
                                        //         useDispatch
                                        //     />
                                        // )}
                                        // filterIcon={() => renderSearchOutlinedButton("division_name_cont")}
                                    />
                                {/* </>
                            )} */}
                            <Table.Column
                                title={renderColumnHeaderTitle("name")}
                                key="name"
                                dataIndex="name"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"name"}
                                        filterType={"name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("vendor_name", "vendor")}
                                key="vendor_id"
                                dataIndex="vendor_name"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"vendor_name"}
                                        filterType={"vendor_name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("vendor_name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("account_number")}
                                key="account_number"
                                dataIndex="account_number"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"account_number"}
                                        filterType={"account_number_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("account_number_cont")}
                            />
                            <Table.Column
                                title="Meter Number(s)"
                                key="services"
                                dataIndex="service_names"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"services_names"}
                                        filterType={"services_meter_number_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("services_meter_number_cont")}
                            />
                            <Table.Column
                                title="Service(s)"
                                key="service_types"
                                dataIndex="service_types"
                                render={(text, record) => {
                                    // console.log(text)
                                    // const services = text.split(',')
                                    return text.map((s, i) => <Tooltip title={`${s.name} (${s.symbol})`}>{getIcon(s.name)}</Tooltip>)
                                }}
                            />
                            <Table.Column
                                title="Supply"
                                key="supply"
                                dataIndex="linked_supply_account_links"
                                render={(links) => {
                                    if (!Array.isArray(links) || links.length === 0) return <span className="supply-column-empty">—</span>
                                    return (
                                        <Tooltip
                                            // NOTE focus trigger + tabIndex -- the tooltip is the only path to these accounts, so it has to be keyboard reachable
                                            trigger={["hover", "focus"]}
                                            title={
                                                <div className="supply-column-tooltip">
                                                    <div className="supply-column-tooltip-label">Linked supply account{links.length > 1 ? "s" : ""}:</div>
                                                    {/* NOTE the link's own client_id, not scopedByClient -- this table also renders unscoped on /billable_accounts */}
                                                    {links.map((link) => (
                                                        <Link
                                                            key={link.id}
                                                            to={`/clients/${link.client_id}/supply_accounts/${link.id}/dashboard`}
                                                            target="_blank"
                                                            rel="noopener noreferrer">
                                                            {link.account_number || `Supply account ${link.id}`}
                                                        </Link>
                                                    ))}
                                                </div>
                                            }>
                                            <span className="supply-column-linked" tabIndex={0} role="button" aria-label={`Linked supply account${links.length > 1 ? "s" : ""}`}>
                                                S
                                            </span>
                                        </Tooltip>
                                    )
                                }}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("tax_id")}
                                key="tax_id"
                                dataIndex="tax_id"
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"tax_id"}
                                        filterType={"tax_id_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("tax_id_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("active")}
                                key="active"
                                dataIndex="active"
                                render={(active) => (active ? "Active" : "Inactive")}
                                filterDropdown={({ visible }) => (
                                    <ColumnToggleFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"active"}
                                        filterType={"active_eq"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("active_eq")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("bills_date", "Last Bill Date")}
                                // title={"Last Bill Date"}
                                key="last_bill_date"
                                render={(record) => (record.last_bills_date ? moment(record.last_bills_date).format("L") : "")}
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"bills_date"}
                                        filterType={"bills_date"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("bills_date_lteq" || "bills_date_gteq")}
                            />
                            {/* {!roles.includes("client_user") && ( */}
                                <Table.Column
                                    title="Copy"
                                    key="last_bill_file_url"
                                    dataIndex="last_bill_file_url"
                                    render={(text, record) => {
                                        if (text) {
                                            return (
                                                <Button type="primary" size="small" onClick={() => window.open(text, "_blank")}>
                                                    <DownloadOutlined />
                                                </Button>
                                            )
                                        }
                                    }}
                                />
                            {/* )} */}
                            {/* <Table.Column
                                title={renderColumnHeaderTitle("created_at")}
                                key="created_at"
                                dataIndex="created_at"
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialBillableAccounts}
                                        filterAction={filterBillableAccounts}
                                        filterColumn={"created_at"}
                                        filterType={"created_at"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        facilityId={facilityId}
                                        visible={visible}
                                        useDispatch
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("created_at_lteq" || "created_at_gteq")}
                                render={(text, record, index) => moment(record.created_at).format("L")}
                            /> */}
                            <Table.Column
                                title="Edit"
                                key="action"
                                render={(text, record) => (
                                    <>
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    <Role action="update" model="billable_account">
                                                        <Menu.Item key="1" icon={<EditOutlined />}>
                                                            <Link to={scopedByClient ? `/clients/${scopedByClient}/billable_accounts/${record.id}/edit` : `/billable_accounts/${record.id}/edit`}>
                                                                Edit Account
                                                            </Link>
                                                        </Menu.Item>
                                                    </Role>

                                                    <Menu.Item key="2" icon={<EyeOutlined />}>
                                                        <Link to={`/clients/${scopedByClient}/billable_accounts/${record.id}/dashboard`}>View</Link>
                                                    </Menu.Item>

                                                    {/* <Menu.Item key="view" icon={<BookOutlined />}>
                                                        <Link to={`/clients/${scopedByClient}/billable_accounts/${record.id}/history`}>History</Link>
                                                    </Menu.Item> */}

                                                    <Role action="destroy" model="billable_account">
                                                        <Menu.Item key="3" icon={<DeleteOutlined />} onClick={() => handleDeleteClick(record.id)}>
                                                            Delete Account
                                                        </Menu.Item>
                                                    </Role>
                                                </Menu>
                                            }>
                                            <Button>
                                                Actions <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    </>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
            {/* <div className="card-footer">
                <div className="card-footer-left"></div>
                <div className="card-footer-right pagination"></div>
            </div> */}
        </div>
    )
}